import ApiConstant from "../../constants/api.constants";
import api from "../api";
import {
  ProductBatchFilterParams,
  ProductBatchSummaryResponse,
  ProductBatchMetadata,
  ProductBatchCreateRequest,
  ProductBatchUpdateRequest,
  OilIntakeLog,
  ProductBatchEditDetails,
  ProductBatchExpireMetadata,
  ProductExpiryStatusFilterDTO,
  ProductExpiryStatusResponseDTO,
  ProductExpiryStatusExportFilterDTO,
} from "../../models/productbatch-model";

class ProductBatchService {
  // Get batch summary with filters and pagination
  getSummary = async (filters: ProductBatchFilterParams): Promise<ProductBatchSummaryResponse> => {
    try {
      const response = await api.post<ProductBatchSummaryResponse>(
        ApiConstant.getProductBatchSummary,
        filters
      );
      return response?.data ?? { totalRecords: 0, batches: [] };
    } catch (error) {
      console.error("Error fetching batch summary:", error);
      throw error;
    }
  };

  // Get metadata (vendors, source types, sub-categories, products)
  getMetadata = async (): Promise<ProductBatchMetadata> => {
    try {
      const response = await api.get<ProductBatchMetadata>(
        ApiConstant.getProductBatchMetadata
      );
      return response?.data ?? { sourceTypes: [], vendors: [], subCategories: [], products: [] };
    } catch (error) {
      console.error("Error fetching batch metadata:", error);
      throw error;
    }
  };

  // Create new batch
  create = async (payload: ProductBatchCreateRequest): Promise<any> => {
    try {
      const response = await api.post(ApiConstant.createProductBatch, payload);
      return response?.data;
    } catch (error) {
      console.error("Error creating batch:", error);
      throw error;
    }
  };

  // Get oil intake logs by byProductId, vendorId, posId
  getOilIntakeLogs = async (vendorId: number, byProductId: number, posId: number): Promise<OilIntakeLog[]> => {
    try {
      const response = await api.get<OilIntakeLog[]>(
        `${ApiConstant.getProductBatchOilIntakeLogs}?vendorId=${vendorId}&byProductId=${byProductId}&posId=${posId}`
      );
      return response?.data ?? [];
    } catch (error) {
      console.error("Error fetching oil intake logs:", error);
      throw error;
    }
  };

  // Get batch details for editing
  getById = async (batchId: number): Promise<ProductBatchEditDetails | null> => {
    try {
      const response = await api.get<ProductBatchEditDetails>(
        `${ApiConstant.getProductBatchById}/${batchId}`
      );
      console.log("🔍 Batch Details API Response:", response?.data);
      return response?.data ?? null;
    } catch (error) {
      console.error("Error fetching batch details:", error);
      throw error;
    }
  };

  // Update batch
  update = async (batchId: number, payload: ProductBatchUpdateRequest): Promise<any> => {
    try {
      const response = await api.put(
        `${ApiConstant.updateProductBatch}/${batchId}`,
        payload
      );
      return response?.data;
    } catch (error) {
      console.error("Error updating batch:", error);
      throw error;
    }
  };

  // Delete batch
  delete = async (batchId: number): Promise<any> => {
    try {
      const response = await api.delete(
        `${ApiConstant.deleteProductBatch}/${batchId}`
      );
      return response?.data;
    } catch (error) {
      console.error("Error deleting batch:", error);
      throw error;
    }
  };

  // Export batch data
  export = async (filters: Omit<ProductBatchFilterParams, 'pageNumber' | 'pageSize'>): Promise<any> => {
    try {
      const response = await api.post("/api/ProductBatch/export", filters, {
        responseType: 'blob'
      });
      return response?.data;
    } catch (error) {
      console.error("Error exporting batch data:", error);
      throw error;
    }
  };

 getVendorInvoiceMap = async (vendorId: number, posId?: number, rawMaterialId?: number) => {
  try {
    let url = `${ApiConstant.getvendormapinvoices}/${vendorId}`;
    if (posId && rawMaterialId) {
      url += `?posId=${posId}&rawMaterialId=${rawMaterialId}`;
    }
    const result = await api.get(url);   
    const data = result?.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const invoicesArray = Object.entries(data).map(([key, value]) => ({
        rawMaterialPurchaseId: parseInt(key, 10),
        invoiceNumber: String(value),
      }));
      return invoicesArray;
    }

    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching vendor invoice map:", error);
    return [];
  }
};

getProductBatchExpireMetadata = async (): Promise<ProductBatchExpireMetadata> => {
    try {
      const response = await api.get<ProductBatchExpireMetadata>(
        ApiConstant.productbatchexpirymetadata
      );
      return response?.data ?? { categories: [], posList: [], products: [] };
    } catch (error) {
      console.error("Error fetching batch metadata:", error);
      throw error;
    }
  };

  // Get product expiry status with filters and pagination
  getProductExpiryStatus = async (filters: ProductExpiryStatusFilterDTO): Promise<ProductExpiryStatusResponseDTO> => {
    try {
      const response = await api.post<ProductExpiryStatusResponseDTO>(
        ApiConstant.productbatchexpiryreport,
        filters
      );
      return response?.data ?? { data: [], totalRecords: 0, pageNumber: 1, pageSize: 10, totalPages: 0 };
    } catch (error) {
      console.error("Error fetching product expiry status:", error);
      throw error;
    }
  };

  getExportProductExpiryStatus = async (filters: ProductExpiryStatusExportFilterDTO): Promise<ProductExpiryStatusResponseDTO> => {
  try {
    const response = await api.post<ProductExpiryStatusResponseDTO>(
      ApiConstant.exportproductbatchexpiryreport,
      filters
    );

    return response?.data ?? {
      data: [],
      totalRecords: 0,
      pageNumber: 1,
      pageSize: 0,
      totalPages: 0,
    };
  } catch (error) {
    console.error("Error exporting product expiry status:", error);
    throw error;
  }
};

  updateVisibility = async (batchId: number, isHidden: boolean, actionReason: string): Promise<any> => {
    try {
      const response = await api.post(`${ApiConstant.updateProductBatchVisibility}/${batchId}/visibility`, {
        isHidden,
        actionReason,
      });
      return response?.data;
    } catch (error) {
      console.error("Error updating visibility:", error);
      throw error;
    }
  };

  getVisibilityHistory = async (batchId: number): Promise<any[]> => {
    try {
      const response = await api.get(`${ApiConstant.getProductBatchVisibilityHistory}/${batchId}/visibility-history`);
      return response?.data ?? [];
    } catch (error) {
      console.error("Error fetching visibility history:", error);
      throw error;
    }
  };
}


export default new ProductBatchService();

